import React, { useState, useEffect, useRef } from 'react';
import { Day } from '../types';

interface EditDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  day: Day | null;
  onSave: (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => Promise<void>;
  onSaveAndShowRecommendations: (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => void;
  onAIGenerateWithActivities: (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => void;
  isLoading?: boolean;
  isSaving?: boolean;
  isDeleting?: boolean;
  onAIEdit?: (editDescription: string) => Promise<any>; // For AI mode editing
  isGeneratingAI?: boolean; // For AI mode loading state
}

const EditDayModal: React.FC<EditDayModalProps> = ({
  isOpen,
  onClose,
  day,
  onSave,
  onSaveAndShowRecommendations,
  onAIGenerateWithActivities,
  isLoading = false,
  isSaving = false,
  isDeleting = false,
  onAIEdit,
  isGeneratingAI = false
}) => {
  const [dayDate, setDayDate] = useState('');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [lodgingInfo, setLodgingInfo] = useState('');
  const [color, setColor] = useState('');
  const [isAIMode, setIsAIMode] = useState(true); // AI mode by default when editing
  const [aiEditPrompt, setAiEditPrompt] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const scrollPositionRef = useRef<number>(0);

  // Helper function to trim title to 25 characters
  const trimTitle = (title: string): string => {
    if (!title) return '';
    return title.length > 25 ? title.substring(0, 25) : title;
  };

  // Reset form when modal opens/closes or day changes
  useEffect(() => {
    if (isOpen && day) {
      setDayDate(day.dayDate);
      setTitle(trimTitle(day.title || ''));
      setSummary(day.summary || '');
      setLodgingInfo(day.lodgingInfo || '');
      setColor(day.color || '');
      setIsAIMode(true);
      setAiEditPrompt('');
    } else if (!isOpen) {
      setDayDate('');
      setTitle('');
      setSummary('');
      setLodgingInfo('');
      setColor('');
      setIsAIMode(true);
      setAiEditPrompt('');
    }
  }, [isOpen, day]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      scrollPositionRef.current = window.scrollY;
      // Prevent body scroll immediately
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Cleanup: restore scroll position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollPositionRef.current);
      };
    } else {
      // Restore scroll position when closing
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [isOpen]);

  // Close dropdown when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen) {
        const dropdown = document.querySelector('[data-dropdown="edit-day-save-dropdown"]');
        const button = document.querySelector('[data-button="edit-day-save-button"]');
        
        if (dropdown && !dropdown.contains(event.target as Node) && 
            button && !button.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleAIEdit = async () => {
    if (!aiEditPrompt.trim() || !onAIEdit) {
      alert('Please enter a description of the changes you want to make');
      return;
    }
    
    try {
      const editedDay = await onAIEdit(aiEditPrompt);
      
      if (editedDay) {
        // Populate the form with the edited day data
        setDayDate(editedDay.dayDate || '');
        setTitle(trimTitle(editedDay.title || ''));
        setSummary(editedDay.summary || '');
        setLodgingInfo(editedDay.lodgingInfo || '');
        setColor(editedDay.color || '');
        
        // Clear the AI prompt and switch to manual mode
        setAiEditPrompt('');
        setIsAIMode(false);
      } else {
        console.error('No edited day returned from AI edit');
        alert('Failed to get edited day data');
      }
    } catch (error) {
      console.error('Error editing with AI:', error);
      alert(`Failed to edit day: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDropdownAction = (action: 'save' | 'saveAndRecommendations' | 'saveWithActivities') => {
    setIsDropdownOpen(false);
    
    if (!dayDate || !summary) {
      alert('Please fill in date and day summary');
      return;
    }

    const dayData = {
      dayDate,
      title: trimTitle(title),
      summary,
      lodgingInfo
    };

    switch (action) {
      case 'save':
        onSave(dayData);
        break;
      case 'saveAndRecommendations':
        onSaveAndShowRecommendations(dayData);
        break;
      case 'saveWithActivities':
        onAIGenerateWithActivities(dayData);
        break;
    }
  };

  const handleClose = () => {
    // Reset form
    setDayDate('');
    setTitle('');
    setSummary('');
    setLodgingInfo('');
    setColor('');
    setIsAIMode(true);
    setAiEditPrompt('');
    setIsDropdownOpen(false);
    onClose();
  };

  if (!isOpen || !day) return null;

  const dayTitle = day.title || `Day ${day.dayDate}`;

  return (
    <div 
      className="modal-overlay"
      onWheel={(e) => {
        // Prevent scroll on overlay, but allow it within modal content
        const target = e.target as HTMLElement;
        const modalContent = target.closest('.modal-content');
        // If the event is on the overlay itself (not inside modal content), prevent it
        if (e.currentTarget === e.target || !modalContent) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      style={{ overflow: 'hidden' }}
    >
      <div className="modal-content max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header - styled like Edit Itinerary Item modal */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-[#E3E8E5] flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#0B1412]">
            Edit {dayTitle}
          </h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F2FBF9] transition-colors"
            disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-[#6F7974]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Mode Toggle */}
            <div className="flex items-center justify-between p-4 bg-[#F2FBF9] rounded-xl border border-[#E3E8E5]">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-[#0B1412]">AI Mode</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!isAIMode}
                    onChange={(e) => setIsAIMode(!e.target.checked)}
                    className="sr-only peer"
                    disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
                <span className="text-sm font-medium text-[#0B1412]">Manual Mode</span>
              </div>
            </div>

            {/* AI Mode Content */}
            {isAIMode && (
              <div className="input-group">
                <label className="input-label">
                  What changes would you like to make to this day?
                </label>
                <textarea
                  value={aiEditPrompt}
                  onChange={(e) => setAiEditPrompt(e.target.value)}
                  placeholder="e.g., Change the date to tomorrow, update the summary to include more cultural activities, modify the accommodation details..."
                  className="input-field resize-none"
                  rows={4}
                  disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
                />
              </div>
            )}

            {/* Manual Mode Content */}
            {!isAIMode && (
              <>
                {/* Date Input */}
                <div className="input-group">
                  <label className="input-label">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={dayDate}
                    onChange={(e) => setDayDate(e.target.value)}
                    className="input-field"
                    required
                    disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
                  />
                </div>

                {/* Day Title */}
                <div className="input-group">
                  <label className="input-label">
                    Day Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Short title for this day (max 25 characters)"
                    className="input-field"
                    maxLength={25}
                    disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
                  />
                  <div className="text-xs text-[#6F7974] mt-1">
                    {title.length}/25 characters
                  </div>
                </div>

                {/* Day Summary */}
                <div className="input-group">
                  <label className="input-label">
                    Day Summary *
                  </label>
                  <textarea
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="Describe what this day will include..."
                    className="input-field resize-none"
                    rows={3}
                    required
                    disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
                  />
                </div>

                {/* Accommodation */}
                <div className="input-group">
                  <label className="input-label">
                    Accommodation
                  </label>
                  <input
                    type="text"
                    value={lodgingInfo}
                    onChange={(e) => setLodgingInfo(e.target.value)}
                    placeholder="Hotel name, address, or accommodation details (optional)"
                    className="input-field"
                    disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
                  />
                </div>

                {/* Color (Optional) */}
                <div className="input-group">
                  <label className="input-label">
                    Color on map (Optional)
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={color || '#3B82F6'}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-16 h-10 rounded-lg border border-[#E3E8E5] cursor-pointer"
                      disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
                    />
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      placeholder="#3B82F6"
                      className="input-field flex-1"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-[#E3E8E5] flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isSaving || isDeleting || isLoading || isGeneratingAI}
            className="btn-secondary"
          >
            Cancel
          </button>
          {isAIMode ? (
            <button
              onClick={handleAIEdit}
              disabled={
                isSaving || 
                isDeleting || 
                isLoading || 
                isGeneratingAI ||
                !aiEditPrompt.trim()
              }
              className="btn-primary"
            >
              {isGeneratingAI ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2"></div>
                  Applying Changes...
                </>
              ) : (
                'Apply Changes'
              )}
            </button>
          ) : (
            <div className="relative">
              <button
                data-button="edit-day-save-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(!isDropdownOpen);
                }}
                disabled={isLoading || isSaving || isDeleting || isGeneratingAI || !dayDate || !summary}
                className="btn-primary flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner w-4 h-4 mr-2"></div>
                    Generating...
                  </>
                ) : isSaving ? (
                  <>
                    <div className="loading-spinner w-4 h-4 mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="mr-2">💾</span>
                    Save Day
                    <span className="ml-2">▲</span>
                  </>
                )}
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div 
                  data-dropdown="edit-day-save-dropdown"
                  className="absolute bottom-full right-0 mb-2 bg-white border border-[#E3E8E5] rounded-xl shadow-lg overflow-hidden z-50"
                  onClick={(e) => e.stopPropagation()}
                  style={{ 
                    minWidth: '280px'
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownAction('saveWithActivities');
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#F2FBF9] flex items-center font-medium text-[#0B1412] transition-colors duration-200"
                  >
                    <span className="mr-3">🤖</span>
                    Save + Generate New Activities + Override Existing
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownAction('saveAndRecommendations');
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#F2FBF9] flex items-center font-medium text-[#0B1412] border-t border-[#E3E8E5] transition-colors duration-200"
                  >
                    <span className="mr-3">💡</span>
                    Save + Show Recommendations
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownAction('save');
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#F2FBF9] flex items-center font-medium text-[#0B1412] border-t border-[#E3E8E5] transition-colors duration-200"
                  >
                    <span className="mr-3">📝</span>
                    Save Day Metadata Only + Keep Existing Itinerary Items
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditDayModal;


